import type { ReactNode } from 'react';
import type {
  AssigneeModel,
  AssignmentUser,
  PopulatedSystem,
  PopulatedOrgFunction,
  PopulatedExternalContact,
  ProcessEventNode,
  StepActionType
} from '@bliks-io/contracts';
import { PreviousStepPreview } from '@/components/processes/PreviousStepPreview';

/**
 * Shape of a recorded step the review preview can render. Intentionally lax
 * (most fields optional, unknown for sub-objects) so both the assignment-list
 * `AssignmentProcessStepRecording` and the wider populated recording shape
 * (with a union-shaped `assignedTo`) can flow in unchanged. The preview
 * component is the gatekeeper for what it actually reads.
 */
interface ReviewableProcessStep {
  _id: string;
  actionType: StepActionType;
  generatedName?: string | null;
  generatedDescription?: string | null;
  task?: {
    taskDescription?: string;
    output?: string;
  };
  events?: Array<{
    eventType: string;
    name: string;
    description: string;
  }>;
  rules?: Array<{ _id?: string; output?: string }>;
  assignedTo?: unknown;
  assignedToModel?: AssigneeModel;
}

export interface ReviewProcessStepSectionProps {
  label: string;
  sourceProcessStep: string | ReviewableProcessStep | ProcessEventNode | null;
  assignedTo?:
    | AssignmentUser
    | PopulatedSystem
    | PopulatedOrgFunction
    | PopulatedExternalContact
    | string;
  assignedToModel?: AssigneeModel;
  showOnlyCurrentStep?: boolean;
  showUnclearSuccessorWarning?: boolean;
  ruleIds?: string[];
  /** Forwarded to PreviousStepPreview — replaces the right-side card area. */
  currentStepSlot?: ReactNode;
  /** Forwarded to PreviousStepPreview — renders the "show in process" button. */
  onShowInMap?: () => void;
  /** Forwarded to PreviousStepPreview — draws that button busy. */
  showInMapPending?: boolean;
}

/**
 * Displays a process step preview with consistent section styling
 * Wraps PreviousStepPreview component with heading and conditional transformation based on entity type
 */
export function ReviewProcessStepSection({
  label,
  sourceProcessStep,
  assignedTo,
  assignedToModel,
  showOnlyCurrentStep,
  showUnclearSuccessorWarning,
  ruleIds,
  currentStepSlot,
  onShowInMap,
  showInMapPending
}: ReviewProcessStepSectionProps) {
  // A bare id is not an assignee this preview can draw; the populated shapes
  // pass through untouched, because the card reads them directly.
  const populatedAssignee = typeof assignedTo === 'string' ? undefined : assignedTo;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">{label}</h3>
      <PreviousStepPreview
        sourceProcessStep={sourceProcessStep}
        assignedTo={populatedAssignee}
        assignedToModel={assignedToModel}
        showOnlyCurrentStep={showOnlyCurrentStep}
        showUnclearSuccessorWarning={showUnclearSuccessorWarning}
        ruleIds={ruleIds}
        currentStepSlot={currentStepSlot}
        onShowInMap={onShowInMap}
        showInMapPending={showInMapPending}
      />
    </div>
  );
}
